import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTranslation } from "@/hooks/useTranslation";
import {
  companyIdentifierChecks,
  type CompanyIdentifierCheck,
} from "@/lib/company-mention-reliability";
import { cn } from "@/lib/utils";
import type { CompanyTag } from "@shaarp/common";
import { useEffect, useState, type ReactElement } from "react";

// Short enough to show before the preview card (350 ms), which opens on the
// other side of the chip, so both can be read at once.
const DEBUG_OPEN_DELAY_MS = 200;

/** Why the label renders the way it does — one per branch of `CompanyMention`. */
export type CompanyMentionDebugStatus = "currentCompany" | "tag";

export type CompanyMentionDebugInfo = {
  /** The tag the model wrote, as the chip reads it. */
  marker: CompanyTag;
  status: CompanyMentionDebugStatus;
};

type CompanyMentionDebugPanelProps = {
  debug: CompanyMentionDebugInfo;
};

type CompanyMentionDebugProps = CompanyMentionDebugPanelProps & {
  /** From `useIsCompanyMentionDebugEnabled`, read once by the label. */
  isEnabled: boolean;
  /** Holds the bubble closed while the preview card shows the same panel. */
  isSuppressed?: boolean;
  children: ReactElement;
};

// The warning tint turns amber in dark mode, where the light theme text and
// its muted grey fall to about 2:1 — so the panel keeps dark text on it.
const MUTED_TEXT_CLASS = "text-muted-foreground dark:text-neutral-800";

type DebugRow = {
  labelKey: string;
  value: string | null | undefined;
  /** The verdict on an identifier, absent for values that identify nothing alone. */
  check?: CompanyIdentifierCheck;
};

/** Label and value of each row, with the verdict on the identifiers. */
function DebugRows({ rows }: { rows: DebugRow[] }) {
  const { t } = useTranslation();
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
      {rows.map(({ labelKey, value, check }) => (
        <div key={labelKey} className="contents">
          <dt className={MUTED_TEXT_CLASS}>{t(labelKey)}</dt>
          <dd className="flex flex-col">
            <span className="break-all font-mono">
              {value || t("company.mention.debug.empty")}
            </span>
            {check && (
              <span
                className={cn(
                  "font-medium",
                  check === "valid"
                    ? "text-emerald-700 dark:text-emerald-950"
                    : "text-destructive dark:text-red-950"
                )}
              >
                {t(`company.mention.debug.checks.${check}`)}
              </span>
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * What a company tag carries, the verdict on each identifier and why the label
 * renders the way it does — to debug a mention that shows the wrong company.
 * Unstyled: the bubble and the preview card each give it the warning tint that
 * marks an admin-only surface (see `chat-context-usage.tsx`).
 */
export function CompanyMentionDebugPanel({
  debug,
}: CompanyMentionDebugPanelProps) {
  const { t } = useTranslation();
  const { marker, status } = debug;
  const checks = companyIdentifierChecks(marker);
  const markerRows: DebugRow[] = [
    { labelKey: "company.mention.debug.fields.name", value: marker.name },
    { labelKey: "company.mention.debug.fields.city", value: marker.city },
    {
      labelKey: "company.mention.debug.fields.domain",
      value: marker.domain,
      check: checks.domain,
    },
    {
      labelKey: "company.mention.debug.fields.siren",
      value: marker.siren,
      check: checks.siren,
    },
    {
      labelKey: "company.mention.debug.fields.siret",
      value: marker.siret,
      check: checks.siret,
    },
    {
      labelKey: "company.mention.debug.fields.linkedin",
      value: marker.linkedin,
      check: checks.linkedin,
    },
    {
      labelKey: "company.mention.debug.fields.id",
      value: marker.id,
      check: checks.id,
    },
    {
      labelKey: "company.mention.debug.fields.address",
      value: marker.address,
    },
    {
      labelKey: "company.mention.debug.fields.activity",
      value: marker.activity,
    },
    {
      labelKey: "company.mention.debug.fields.source",
      value: marker.source?.join(" "),
    },
    { labelKey: "company.mention.debug.fields.brief", value: marker.brief },
  ];

  return (
    <div className="flex flex-col gap-2 text-xs text-foreground select-text dark:text-neutral-950">
      <p className="font-semibold">{t("company.mention.debug.title")}</p>
      <p>
        <span className={MUTED_TEXT_CLASS}>
          {t("company.mention.debug.statusLabel")}
        </span>{" "}
        <span className="font-medium">
          {t(`company.mention.debug.status.${status}`)}
        </span>
      </p>
      <section className="flex flex-col gap-1">
        <p className="font-medium">{t("company.mention.debug.markerTitle")}</p>
        <DebugRows rows={markerRows} />
      </section>
    </div>
  );
}

/**
 * Admin-only hover bubble holding `CompanyMentionDebugPanel`, under the label.
 * When `isEnabled` is off (outside admin mode, on a phone) it renders its child
 * untouched. Once the preview card is up the bubble is held closed
 * (`isSuppressed`) and the card shows the panel instead: both float on the same
 * side of a chip low on the screen, and the card, opened last, would cover the
 * bubble.
 *
 * The child must be a single element that forwards its ref and props (a DOM
 * element or a Radix `asChild` part), as it becomes the tooltip trigger.
 */
export function CompanyMentionDebug({
  debug,
  isEnabled,
  isSuppressed = false,
  children,
}: CompanyMentionDebugProps) {
  const [isOpen, setIsOpen] = useState(false);

  // While held closed, the tooltip drops the close it is sent when the cursor
  // leaves (its `open` already reads false), so `isOpen` would stay true and
  // the bubble pop back once the card closes. Forget it instead.
  useEffect(() => {
    if (isSuppressed) setIsOpen(false);
  }, [isSuppressed]);

  if (!isEnabled) return children;

  return (
    <TooltipProvider>
      <Tooltip
        delayDuration={DEBUG_OPEN_DELAY_MS}
        open={isOpen && !isSuppressed}
        onOpenChange={setIsOpen}
      >
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent
          side="bottom"
          align="start"
          className="w-80 border border-warning bg-warning-foreground p-3"
        >
          <CompanyMentionDebugPanel debug={debug} />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
